import React, { useEffect, useState } from 'react';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';
import { MEDICAL_HISTORY_CONTAINER } from '../MedicalHistoryContainer';
import { PAD_V2_CONTAINER } from '../PadContainer';
import {
    generateFrequencyOptions,
    generateSinceOptions,
} from '../../../../common/optionGeneratorUtils';
import {
    MEDICAL_HISTORY_CATEGORY,
    MHC,
    MHFrequency,
    MHItem,
    MHItem2,
    MHSince,
} from '../../../../../store/types/medicalHistory';
import MedicalHistoryCommonItemsIcon from '../../../../common/MedicalHistoryCommonItemsIcon';
import { EditableCell } from './MedTableRenderer';

const CommonItemsRender: React.FC<{
    medicalHistoryConfigCommonItemsData: MHItem;
    patientHistoryData: MHC | undefined;
    isMedicalHistorySection?: boolean;
    index: number;
    onItemSave: (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2) => void;
    onItemDelete: (catId: MEDICAL_HISTORY_CATEGORY, itemId: string) => void;
    medicalHistoryOptions: any;
    StatusMessage: {
        [key: string]: string;
    };
}> = ({
    medicalHistoryConfigCommonItemsData,
    onItemDelete,
    onItemSave,
    patientHistoryData,
    isMedicalHistorySection,
    medicalHistoryOptions,
    StatusMessage,
}) => {
    const TARGET_CONTAINER = isMedicalHistorySection ? MEDICAL_HISTORY_CONTAINER : PAD_V2_CONTAINER;
    let ref: EkaFocosableElement | undefined;
    const [refOnActive, setRefOnActive] = useState(false);

    const presentItem =
        medicalHistoryConfigCommonItemsData?.catId &&
        (patientHistoryData?.[medicalHistoryConfigCommonItemsData?.catId]?.find(
            (st) => st.id === medicalHistoryConfigCommonItemsData.id,
        ) as MHItem);

    const onSearch = async (text: string): Promise<{ id: string; label: string }[]> => {
        return new Promise((resolve) => {
            resolve(generateSinceOptions(text));
        });
    };

    const saveItemsOnMedicalHistory = (status: 'Active' | 'Inactive') => {
        const { catId } = medicalHistoryConfigCommonItemsData;
        const item = presentItem ? presentItem : medicalHistoryConfigCommonItemsData;
        const cloneItem = { ...item };
        delete cloneItem?.catId;
        catId && onItemSave(catId, { ...cloneItem, status: status });
    };

    const deleteItemsFromMedicalHistory = () => {
        const { catId, id } = medicalHistoryConfigCommonItemsData;
        catId && onItemDelete(catId, id);
    };

    useEffect(() => {
        if (refOnActive) {
            ref?.focus();
            setRefOnActive(false);
        }
    }, [presentItem?.status]);

    const changeStatus = () => {
        switch (presentItem?.status?.toLowerCase()) {
            case 'active':
                saveItemsOnMedicalHistory('Inactive');
                break;
            case 'inactive':
                deleteItemsFromMedicalHistory();
                break;
            default:
                saveItemsOnMedicalHistory('Active');
                setRefOnActive(true);
        }
    };

    const onFrequencySearch = async (text: string): Promise<{ id: string; label: string }[]> => {
        const val = await new Promise((resolve) => {
            resolve(generateFrequencyOptions(text));
        });
        return new Promise((resolve) => {
            resolve(generateFrequencyOptions(text));
        });
    };

    return (
        <div className="flex w-full">
            <div>
                <MedicalHistoryCommonItemsIcon
                    status={presentItem?.status?.toLowerCase()}
                    onClickIcon={changeStatus}
                    onClickText={changeStatus}
                    statusMessage={
                        presentItem?.status?.toLowerCase()
                            ? StatusMessage[presentItem?.status?.toLowerCase()]
                            : '-'
                    }
                    text={medicalHistoryConfigCommonItemsData.name}
                />
            </div>
            <div>
                {medicalHistoryConfigCommonItemsData.catId &&
                    medicalHistoryOptions[medicalHistoryConfigCommonItemsData.catId].display ===
                        'since' &&
                    presentItem?.status?.toLowerCase() === 'active' && (
                        <div className="w-full border border-darwin-neutral-100 mr-10 min-w-80">
                            <EditableCell
                                containerId={TARGET_CONTAINER}
                                customDropdownWidth={100}
                                fieldName={`sdp1-13`}
                                label="Since"
                                withoutError
                                setRef={(sinceRef) => {
                                    ref = sinceRef;
                                }}
                                customDropdownClass="font-lato"
                                placeholder="Since"
                                debounceDelay={0}
                                onSearch={onSearch}
                                selectedValue={{
                                    id: (presentItem as MHSince)?.since?.custom || '',
                                    label: (presentItem as MHSince)?.since?.custom || '',
                                }}
                                getLabelToCompare={(o) => o.label}
                                onSelect={(option) => {
                                    option &&
                                        medicalHistoryConfigCommonItemsData.catId &&
                                        onItemSave(medicalHistoryConfigCommonItemsData.catId, {
                                            ...presentItem,
                                            since: { custom: option.label },
                                        });
                                }}
                            />
                        </div>
                    )}
                {medicalHistoryConfigCommonItemsData.catId &&
                    medicalHistoryOptions[medicalHistoryConfigCommonItemsData.catId].display ===
                        'frequency' &&
                    presentItem?.status?.toLowerCase() === 'active' && (
                        <div className="w-full border border-darwin-neutral-100 mr-10 min-w-80">
                            <EditableCell
                                containerId={TARGET_CONTAINER}
                                customDropdownWidth={150}
                                fieldName={`sdp1-14`}
                                label="Frequency"
                                withoutError
                                setRef={(frequencyRef) => {
                                    ref = frequencyRef;
                                }}
                                customDropdownClass="font-lato"
                                placeholder="Frequency"
                                debounceDelay={0}
                                onSearch={onFrequencySearch}
                                selectedValue={{
                                    id: (presentItem as MHFrequency)?.frequency?.custom || '',
                                    label: (presentItem as MHFrequency)?.frequency?.custom || '',
                                }}
                                getLabelToCompare={(o) => o.label}
                                onSelect={(option) => {
                                    option &&
                                        medicalHistoryConfigCommonItemsData.catId &&
                                        onItemSave(medicalHistoryConfigCommonItemsData.catId, {
                                            ...presentItem,
                                            frequency: { custom: option.label },
                                        });
                                }}
                            />
                        </div>
                    )}
            </div>
        </div>
    );
};

export default CommonItemsRender;
